import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { useStationMode } from '@hooks/station';
import { useFavorites } from '@hooks/useFavorites';
import { sendSelectCompanionMessage } from '@utils/station';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { getContributorById } from '../../services/api';
import { ContributorProfileFeeds } from './components/ContributorProfileFeeds';

interface ContributorData {
  id: string;
  givenName: string;
  thumbnailUrl: string;
  videoDesktopUrl?: string;
  videoMobileUrl?: string;
  genre: string[];
  subGenre: string[];
  type: string;
  likes: number;
  gender: string;
  age: number;
  city: string;
  isLiked?: boolean;
  companionSlug?: string;
  companionId?: string;
  albums: Array<{
    id: string;
    name: string;
    tracksCount: number;
    thumbnailUrl: string;
    duration: number;
    locales: string[];
    category?: string;
  }>;
}

export const ContributorProfile = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isStationMode = useStationMode();
  const { isMinimized, setIsMinimized } = useCommonState();
  const { toggleFavorite } = useFavorites();
  const { activePlaylist } = useMusicState();
  const [contributor, setContributor] = useState<ContributorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);

  const { setCompanionSlug } = useSessionFeatureState();

  // Only minimize music player if it's currently maximized and playing
  // This preserves music when coming from player, but allows maximized player when coming from home
  useEffect(() => {
    if (activePlaylist) {
      console.log('Active playlist', activePlaylist);
      // Only minimize if music player is currently maximized (not already minimized)
      // This means user came from music player and we want to preserve the music
      if (!isMinimized) {
        setIsMinimized(true);
      }
      // If already minimized, don't change it - user came from home/featured
    }
  }, []);

  // Fetch contributor data
  useEffect(() => {
    const fetchContributor = async () => {
      // Get ID from URL if not available in params
      const contributorId =
        id || window.location.pathname.split('/contributor/')[1];

      if (!contributorId) {
        setError(t('contributor.noIdProvided'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);


      try {
        const data = await getContributorById(contributorId);

        if (isStationMode && data?.companionId) {
          sendSelectCompanionMessage(data.companionId);
          setLoading(false);
        }

        if (data.companionSlug && !isStationMode) {
          setCompanionSlug({
            companionSlug: data.companionSlug,
            isSessionPage: false,
          });
        }

        if (data) {
          setContributor(data);
          // Check if contributor has isLiked property from API
          const isContributorLiked = data.isLiked ?? false;
          setIsLiked(isContributorLiked);
        } else {
          setError(t('contributor.notFound'));
        }
      } catch (err) {
        console.error('Error fetching contributor:', err);
        setError(t('contributor.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    fetchContributor();
  }, [id, isStationMode]);

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(async () => {
    if (!contributor) return;

    try {
      // Optimistically update the UI
      setIsLiked((prev) => !prev);

      // Call the toggle favorite function with current isLiked state
      await toggleFavorite(
        'contributor',
        contributor.id,
        isLiked,
        contributor.givenName,
        contributor.givenName, // For contributors, creator_name is the same as content_name
        'creator', // Contributors use 'creator' category
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert the optimistic update on error
      setIsLiked((prev) => !prev);
    }
  }, [contributor, toggleFavorite, isLiked]);

  // Show loading or error state within the page layout
  if (loading || error || !contributor) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-99999">
        {loading ? (
          <NapsterSpinner />
        ) : (
          <div className="text-center max-w-lg px-6">
            <img
              src="/assets/images/logo_napster_white.svg"
              className="h-[26px] mx-auto mb-10"
              alt="Napster"
            />
            <p className="text-white text-2xl md:text-3xl font-bold leading-snug mb-8">
              {error || t('contributor.notFound')}
            </p>
            <Link
              to="/"
              className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-lg text-sm font-medium transition-colors duration-200">
              {t('generation.goHomeButton')}
            </Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto pt-0 sm:pt-[65px]">
      {/* Single instance of ContributorProfileFeeds - handles both desktop and mobile */}
      <ContributorProfileFeeds
        contributor={contributor}
        isLiked={isLiked}
        onFavoriteToggle={handleFavoriteToggle}
      />
    </div>
  );
});

ContributorProfile.displayName = 'ContributorProfile';
